import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useTranslation } from "react-i18next";
import { Bike, Car, Truck, Snowflake } from "lucide-react";
import { ServiceOptionCard } from "./ServiceOptionCard";
import { GLOBAL_PRODUCT, catalogOrderIndex } from "../lib/productCatalog";

// Category-scoped Lucide icons so Delivery (and future categories that
// only differ by vehicle class) render distinguishable option cards
// instead of identical placeholder circles.
const CATEGORY_OPTION_ICONS = {
  delivery: {
    motorcycle: <Bike size={22} className="text-white/80" />,
    car:        <Car size={22} className="text-white/80" />,
    van:        <Truck size={22} className="text-white/80" />,
    truck:      <Truck size={22} className="text-white/80" />,
  },
  moving: {
    small_van:           <Truck size={22} className="text-white/80" />,
    medium_truck:        <Truck size={22} className="text-white/80" />,
    large_truck:         <Truck size={22} className="text-white/80" />,
    xl_truck:            <Truck size={22} className="text-white/80" />,
    refrigerated_van:    <Snowflake size={22} className="text-cyan-300" />,
    refrigerated_truck:  <Snowflake size={22} className="text-cyan-300" />,
  },
};

/**
 * ServiceOptionGrid — data-driven picker used across ALL non-taxi
 * categories (delivery, moving, towing, mechanic, tire, carwash …).
 *
 * Fetches available option_codes from
 *   GET /api/pricing/options/{country}/{category}
 * — sourced entirely from active Pricing V2 rules. When admins add or
 * rename options in the DB the UI updates automatically without a
 * rebuild.
 *
 * When `pricingContext` (pickup + dropoff coords) is provided, the
 * grid ALSO calls `/api/fare/estimate` once per option — so each card
 * shows its own per-option price, matching the Taxi UX in
 * RideRequest.jsx. Rate-limited to serial requests for gentle
 * behaviour on the ingress.
 *
 * Props:
 *   country: string          (e.g. "IL")
 *   category: string         (e.g. "towing")
 *   value: string|null       (selected option_code)
 *   onChange: (opt) => void  (parent stores selection and passes to
 *                             /api/fare/estimate as option_code)
 *   pricingContext?: {
 *     pickup_lat, pickup_lng, dropoff_lat, dropoff_lng, quantity?,
 *     pickup_at?, booking_source?,
 *   }
 *   fmtFare?: (n, cur) => string
 *   testId?: string
 *
 * The grid does NOT calculate prices — every price comes from
 * /api/fare/estimate via Pricing V2.
 */
export function ServiceOptionGrid({
  country, category, value, onChange, pricingContext, fmtFare, testId,
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [priceByOption, setPriceByOption] = useState({});

  // 1. Build the option list from the GLOBAL PRODUCT CATALOG (source
  // of truth). This is country-agnostic — every country shows the same
  // options for a given category. Pricing (below) is looked up per
  // country and, if missing, the card shows a "Pricing coming soon"
  // chip instead of being hidden.
  useEffect(() => {
    if (!category) return;
    const globalCodes = GLOBAL_PRODUCT[category] || [];
    if (globalCodes.length === 0) { setOptions([]); return; }
    // Human-readable English fallbacks — used only when i18n misses the
    // key. Matches the labels wired into the i18n block for each option.
    const DISPLAY_DEFAULT = {
      towing: { "1t": "1T", "3.5t": "3.5T", "3.5_to_7.5t": "3.5–7.5T", "over_7.5t": "Over 7.5T", "12t_plus": "12T+" },
      tire:     { roadside_repair: "Roadside Repair" },
      carwash:  { exterior: "Exterior Wash", exterior_interior: "Exterior + Interior", premium: "Premium Wash" },
      mechanic: { standard: "Standard" },
      delivery: { motorcycle: "Motorcycle", car: "Car" },
      moving: {
        small_van: "Small Van", medium_truck: "Medium Truck", large_truck: "Large Truck",
        xl_truck: "Heavy Truck", refrigerated_van: "Refrigerated Van", refrigerated_truck: "Refrigerated Truck",
      },
      bus:    { minivan: "Minivan", minibus: "Minibus", bus_standard: "Standard Bus" },
      crane:  { crane_12_18m: "12–18m", crane_18_24m: "18–24m", crane_24_42m: "24–42m", crane_42m_plus: "42m+" },
      taxi:   { regular: "Regular", hybrid: "Hybrid", "7seats": "7 seats", vip: "VIP" },
    };
    const defaults = DISPLAY_DEFAULT[category] || {};
    const opts = globalCodes
      .slice()
      .sort((a, b) => catalogOrderIndex(category, a) - catalogOrderIndex(category, b))
      .map((code) => ({
        option_code: code,
        // Multi-key i18n lookup — some locales nest `serviceOptions` under
        // `request.` or `categories.` (historical).
        name: t(
          [
            `serviceOptions.${category}.${code}`,
            `request.serviceOptions.${category}.${code}`,
            `categories.serviceOptions.${category}.${code}`,
          ],
          defaults[code] || code
        ),
        description: t(
          [
            `serviceOptions.${category}_desc.${code}`,
            `request.serviceOptions.${category}_desc.${code}`,
            `categories.serviceOptions.${category}_desc.${code}`,
          ],
          { defaultValue: "" }
        ) || undefined,
      }));
    setOptions(opts);
  }, [category, t]);

  // 2. Discover which of the global options have pricing in this
  // customer's country. Countries missing a rule for a given option
  // show a "coming soon" chip on that card (never hide it).
  const [countryAvailable, setCountryAvailable] = useState(new Set());
  useEffect(() => {
    if (!country || !category) return;
    let cancelled = false;
    api.get(`/pricing/options/${encodeURIComponent(country)}/${encodeURIComponent(category)}`)
      .then((r) => {
        if (cancelled) return;
        const avail = new Set((r.data?.options || []).map((o) => o.option_code));
        setCountryAvailable(avail);
      })
      .catch(() => { if (!cancelled) setCountryAvailable(new Set()); });
    return () => { cancelled = true; };
  }, [country, category]);

  // 3. Fetch per-option prices for options that DO have country pricing.
  // Options missing from `countryAvailable` are rendered with a
  // "pricing coming soon" chip instead.
  const ctxKey = useMemo(() => JSON.stringify(pricingContext || null), [pricingContext]);
  useEffect(() => {
    if (!pricingContext?.pickup_lat || !pricingContext?.dropoff_lat) {
      setPriceByOption({}); return;
    }
    if (options.length === 0) return;
    let cancelled = false;
    const next = {};
    (async () => {
      for (const opt of options) {
        if (cancelled) return;
        if (!countryAvailable.has(opt.option_code)) {
          next[opt.option_code] = { estimate: null, unavailable: true };
          if (!cancelled) setPriceByOption({ ...next });
          continue;
        }
        try {
          const { data } = await api.post("/fare/estimate", {
            category, country,
            pickup_lat: pricingContext.pickup_lat, pickup_lng: pricingContext.pickup_lng,
            dropoff_lat: pricingContext.dropoff_lat, dropoff_lng: pricingContext.dropoff_lng,
            quantity: pricingContext.quantity || 1,
            use_routing: true,
            option_code: opt.option_code,
            pickup_at: pricingContext.pickup_at,
            booking_source: pricingContext.booking_source || "app",
          });
          next[opt.option_code] = { estimate: data?.estimate ?? null, currency: data?.currency };
        } catch { next[opt.option_code] = { estimate: null }; }
        if (!cancelled) setPriceByOption({ ...next });
      }
    })();
    return () => { cancelled = true; };
  }, [ctxKey, options, category, country, countryAvailable]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (options.length === 0) return null;

  return (
    <div className="mb-4" data-testid={testId || `service-options-${category}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">
        {t(`serviceOptions.${category}.label`, t("serviceOptions.chooseOption", "Choose an option"))}
      </div>
      <div className="-mx-4 px-4">
        <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x pb-1">
          {options.map((opt) => {
            const priced = priceByOption[opt.option_code];
            const catIcons = CATEGORY_OPTION_ICONS[category] || {};
            const isUnavailableInCountry = countryAvailable.size > 0 && !countryAvailable.has(opt.option_code);
            const badge = isUnavailableInCountry
              ? t("common.pricingComingSoon", "Pricing coming soon")
              : opt.option_code === "vip" ? "VIP"
              : opt.option_code === "premium" ? "Popular"
              : null;
            return (
              <ServiceOptionCard
                key={opt.option_code}
                option={{
                  ...opt,
                  icon: catIcons[opt.option_code] || undefined,
                  price: isUnavailableInCountry ? null : (priced?.estimate ?? null),
                  currency: priced?.currency ?? opt.currency,
                  meta: isUnavailableInCountry ? t("common.pricingComingSoon", "Pricing coming soon") : undefined,
                  badge,
                }}
                selected={value === opt.option_code}
                onSelect={isUnavailableInCountry ? undefined : onChange}
                fmtFare={fmtFare}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
